/**
 * Action Taxonomy Definitions & Categorization
 * Canonical 8-action model: view, create, edit, delete, assign, approve, export, import
 */
export const CANONICAL_ACTIONS = ['view', 'create', 'edit', 'delete', 'archive', 'approve', 'export', 'import']

export const ACTION_CATEGORIES = {
  ALL: 'all',
  VIEW: 'view',
  WRITE: 'write',
  APPROVE: 'approve',
}

// Write actions: any of these means the role has modification rights
export const WRITE_ACTION_TYPES = new Set([
  'create',
  'edit',
  'delete',
  'archive',
  'export',
  'import',
])

export const ROLE_CAPABILITIES = {
  VIEW_ONLY: {
    key: 'VIEW_ONLY',
    label: 'Read-Only',
    description: 'Can only view data. Zero creation, modification, deletion, or approval rights.',
    badgeClass: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
    icon: 'visibility',
  },
  VIEW_APPROVE: {
    key: 'VIEW_APPROVE',
    label: 'View + Approve',
    description: 'Can view data and execute designated approval/rejection workflows. Cannot create/edit/delete.',
    badgeClass: 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/30',
    icon: 'verified',
  },
  FULL_ACCESS: {
    key: 'FULL_ACCESS',
    label: 'Full / Modify',
    description: 'Possesses creation, editing, deletion, or operational administrative rights.',
    badgeClass: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30',
    icon: 'edit_square',
  },
  EMPTY: {
    key: 'EMPTY',
    label: 'No Rights',
    description: 'No active rights assigned to this role.',
    badgeClass: 'bg-surface-container-high text-outline border-outline-variant/30',
    icon: 'block',
  },
}

/**
 * Evaluates the capability archetype of a role based on its granted privileges or action types.
 *
 * @param {Array<number|string>} selectedIds - List of selected privilege IDs
 * @param {Array<Object>} tree - Full privilege taxonomy tree
 * @returns {Object} Archetype metadata from ROLE_CAPABILITIES
 */
export function evaluateRoleCapability(selectedIds, tree) {
  if (!selectedIds || selectedIds.length === 0) {
    return ROLE_CAPABILITIES.EMPTY
  }

  const selectedSet = new Set(selectedIds.map(Number))
  let hasWrite = false
  let hasApprove = false
  let hasView = false

  for (const moduleObj of tree || []) {
    for (const sub of moduleObj.submodules || []) {
      for (const act of sub.actions || []) {
        if (selectedSet.has(Number(act.id))) {
          const actType = String(act.type || '').toLowerCase()
          if (WRITE_ACTION_TYPES.has(actType)) {
            hasWrite = true
          } else if (actType === 'approve') {
            hasApprove = true
          } else if (actType === 'view') {
            hasView = true
          }
        }
      }
    }
  }

  if (hasWrite) return ROLE_CAPABILITIES.FULL_ACCESS
  if (hasApprove) return ROLE_CAPABILITIES.VIEW_APPROVE
  if (hasView) return ROLE_CAPABILITIES.VIEW_ONLY
  return ROLE_CAPABILITIES.FULL_ACCESS
}

/**
 * Computes preset IDs for one-click assignment.
 *
 * @param {Array<Object>} tree - Full privilege taxonomy tree
 * @param {'VIEW_ONLY' | 'VIEW_APPROVE' | 'STRIP_WRITES'} presetType
 * @param {Array<number>} currentSelectedIds - Current selection to preserve when stripping writes
 * @returns {Array<number>} New list of selected IDs
 */
export function computePresetPrivilegeIds(tree, presetType, currentSelectedIds = []) {
  const result = new Set()

  if (presetType === 'VIEW_ONLY') {
    for (const mod of tree || []) {
      for (const sub of mod.submodules || []) {
        for (const act of sub.actions || []) {
          if (String(act.type || '').toLowerCase() === 'view') {
            result.add(Number(act.id))
          }
        }
      }
    }
    return Array.from(result)
  }

  if (presetType === 'VIEW_APPROVE') {
    for (const mod of tree || []) {
      for (const sub of mod.submodules || []) {
        for (const act of sub.actions || []) {
          const actType = String(act.type || '').toLowerCase()
          if (actType === 'view' || actType === 'approve') {
            result.add(Number(act.id))
          }
        }
      }
    }
    return Array.from(result)
  }

  if (presetType === 'STRIP_WRITES') {
    // Keep currently selected IDs, but remove any that are write actions
    const currentSet = new Set((currentSelectedIds || []).map(Number))
    for (const mod of tree || []) {
      for (const sub of mod.submodules || []) {
        for (const act of sub.actions || []) {
          const actType = String(act.type || '').toLowerCase()
          if (WRITE_ACTION_TYPES.has(actType)) {
            currentSet.delete(Number(act.id))
          }
        }
      }
    }
    return Array.from(currentSet)
  }

  return currentSelectedIds
}

/**
 * Filter tree by action category (View Only, Writes, Approvals)
 *
 * @param {Array<Object>} tree
 * @param {string} category - 'all' | 'view' | 'write' | 'approve'
 * @returns {Array<Object>} Filtered tree
 */
export function filterTreeByActionCategory(tree, category) {
  if (!category || category === ACTION_CATEGORIES.ALL) {
    return tree || []
  }

  return (tree || [])
    .map((mod) => {
      const filteredSubmodules = (mod.submodules || [])
        .map((sub) => {
          const filteredActions = (sub.actions || []).filter((act) => {
            const actType = String(act.type || '').toLowerCase()
            if (category === ACTION_CATEGORIES.VIEW) {
              return actType === 'view'
            }
            if (category === ACTION_CATEGORIES.APPROVE) {
              return actType === 'approve'
            }
            if (category === ACTION_CATEGORIES.WRITE) {
              return WRITE_ACTION_TYPES.has(actType)
            }
            return true
          })

          if (filteredActions.length === 0) return null
          return {
            ...sub,
            actions: filteredActions,
          }
        })
        .filter(Boolean)

      if (filteredSubmodules.length === 0) return null
      return {
        ...mod,
        submodules: filteredSubmodules,
      }
    })
    .filter(Boolean)
}

/**
 * Cross-submodule hierarchical dependencies (Option A).
 * Dependent submodules require their parent module's View permission to be operational.
 */
export const SUBMODULE_DEPENDENCIES = {
  customer_profitability: {
    parentKey: 'project_financials',
    parentLabel: 'Project Financials',
    requiredActionType: 'view',
  },
  amc_billing_schedule: {
    parentKey: 'project_financials',
    parentLabel: 'Project Financials',
    requiredActionType: 'view',
  },
}

